const finiteNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clamp = (value, minimum, maximum) => (
  Math.min(Math.max(value, minimum), Math.max(minimum, maximum))
);

/**
 * Keep the floating notes window visible without covering the fixed meeting
 * controls at the bottom of the viewport.
 */
export const clampMeetingNotesWindowPosition = ({
  left,
  top,
  width,
  height,
  viewportWidth,
  viewportHeight,
  margin = 8,
  topInset = 8,
  bottomInset = 92,
} = {}) => {
  const safeMargin = Math.max(0, finiteNumber(margin, 8));
  const safeTopInset = Math.max(safeMargin, finiteNumber(topInset, safeMargin));
  const safeBottomInset = Math.max(safeMargin, finiteNumber(bottomInset, safeMargin));
  const safeViewportWidth = Math.max(0, finiteNumber(viewportWidth, 0));
  const safeViewportHeight = Math.max(0, finiteNumber(viewportHeight, 0));
  const safeWidth = Math.max(0, finiteNumber(width, 0));
  const safeHeight = Math.max(0, finiteNumber(height, 0));
  const maximumLeft = Math.max(
    safeMargin,
    safeViewportWidth - safeWidth - safeMargin
  );
  const maximumTop = Math.max(
    safeTopInset,
    safeViewportHeight - safeHeight - safeBottomInset
  );

  return {
    left: clamp(finiteNumber(left, safeMargin), safeMargin, maximumLeft),
    top: clamp(finiteNumber(top, safeTopInset), safeTopInset, maximumTop),
  };
};

export const getDefaultMeetingNotesWindowPosition = (options = {}) => (
  clampMeetingNotesWindowPosition({
    ...options,
    left: options.margin ?? 8,
    top: options.defaultTop ?? 64,
  })
);

/**
 * Bound a bottom-right resize gesture to the visible meeting workspace.
 * The minimum automatically shrinks on very small screens rather than
 * pushing the resize handle outside the viewport.
 */
export const clampMeetingNotesWindowSize = ({
  width,
  height,
  left = 8,
  top = 64,
  viewportWidth,
  viewportHeight,
  margin = 8,
  bottomInset = 92,
  minWidth = 300,
  minHeight = 280,
} = {}) => {
  const safeMargin = Math.max(0, finiteNumber(margin, 8));
  const safeLeft = Math.max(safeMargin, finiteNumber(left, safeMargin));
  const safeTop = Math.max(safeMargin, finiteNumber(top, safeMargin));
  const availableWidth = Math.max(
    0,
    finiteNumber(viewportWidth, 0) - safeLeft - safeMargin
  );
  const availableHeight = Math.max(
    0,
    finiteNumber(viewportHeight, 0) - safeTop - Math.max(
      safeMargin,
      finiteNumber(bottomInset, 92)
    )
  );
  const effectiveMinWidth = Math.min(
    availableWidth,
    Math.max(0, finiteNumber(minWidth, 300))
  );
  const effectiveMinHeight = Math.min(
    availableHeight,
    Math.max(0, finiteNumber(minHeight, 280))
  );

  return {
    width: clamp(
      finiteNumber(width, effectiveMinWidth),
      effectiveMinWidth,
      availableWidth
    ),
    height: clamp(
      finiteNumber(height, effectiveMinHeight),
      effectiveMinHeight,
      availableHeight
    ),
  };
};
